export interface PlayerStats {
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  handsPlayed: number;
  timesBid: number;
  timesSet: number;
  bidSuccessRate: number;
  totalPointsScored: number;
  bestHand: number;

  /** Sum of bid values (2–4) for hands where we were the bidder and bid was recorded. */
  bidValueSum: number;
  /** Count of bidder hands that contributed to bidValueSum (avoids skewing legacy timesBid). */
  bidValueCount: number;

  /** Points / hands when our team held the bid. */
  pointsBidding: number;
  handsBidding: number;
  /** Points / hands when our team was defending. */
  pointsDefending: number;
  handsDefending: number;

  /** Solo quick-play rubber counters. */
  soloGamesPlayed: number;
  soloGamesWon: number;
  soloGamesLost: number;
  soloHandsPlayed: number;

  /** Online / partner rubber counters. */
  partnerGamesPlayed: number;
  partnerGamesWon: number;
  partnerGamesLost: number;
  partnerHandsPlayed: number;

  /**
   * Recent bid outcomes for form (1 = made, 0 = set), oldest → newest.
   * Capped at RECENT_BID_WINDOW.
   */
  recentBidOutcomes: number[];
}

export const RECENT_BID_WINDOW = 20;

export const EMPTY_PLAYER_STATS: PlayerStats = {
  gamesPlayed: 0,
  gamesWon: 0,
  gamesLost: 0,
  handsPlayed: 0,
  timesBid: 0,
  timesSet: 0,
  bidSuccessRate: 0,
  totalPointsScored: 0,
  bestHand: 0,
  bidValueSum: 0,
  bidValueCount: 0,
  pointsBidding: 0,
  handsBidding: 0,
  pointsDefending: 0,
  handsDefending: 0,
  soloGamesPlayed: 0,
  soloGamesWon: 0,
  soloGamesLost: 0,
  soloHandsPlayed: 0,
  partnerGamesPlayed: 0,
  partnerGamesWon: 0,
  partnerGamesLost: 0,
  partnerHandsPlayed: 0,
  recentBidOutcomes: [],
};

function num(v: unknown): number {
  return Number(v) || 0;
}

function normalizeOutcomes(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(v => (Number(v) === 1 ? 1 : 0))
    .slice(-RECENT_BID_WINDOW);
}

export function normalizePlayerStats(raw: Partial<PlayerStats> | undefined | null): PlayerStats {
  return {
    gamesPlayed: num(raw?.gamesPlayed),
    gamesWon: num(raw?.gamesWon),
    gamesLost: num(raw?.gamesLost),
    handsPlayed: num(raw?.handsPlayed),
    timesBid: num(raw?.timesBid),
    timesSet: num(raw?.timesSet),
    bidSuccessRate: num(raw?.bidSuccessRate),
    totalPointsScored: num(raw?.totalPointsScored),
    bestHand: num(raw?.bestHand),
    bidValueSum: num(raw?.bidValueSum),
    bidValueCount: num(raw?.bidValueCount),
    pointsBidding: num(raw?.pointsBidding),
    handsBidding: num(raw?.handsBidding),
    pointsDefending: num(raw?.pointsDefending),
    handsDefending: num(raw?.handsDefending),
    soloGamesPlayed: num(raw?.soloGamesPlayed),
    soloGamesWon: num(raw?.soloGamesWon),
    soloGamesLost: num(raw?.soloGamesLost),
    soloHandsPlayed: num(raw?.soloHandsPlayed),
    partnerGamesPlayed: num(raw?.partnerGamesPlayed),
    partnerGamesWon: num(raw?.partnerGamesWon),
    partnerGamesLost: num(raw?.partnerGamesLost),
    partnerHandsPlayed: num(raw?.partnerHandsPlayed),
    recentBidOutcomes: normalizeOutcomes(raw?.recentBidOutcomes),
  };
}

export function pointsAverage(stats: PlayerStats): number | null {
  if (stats.handsPlayed <= 0) return null;
  return stats.totalPointsScored / stats.handsPlayed;
}

/** Hands where this player was the bidder and made their bid. */
export function bidsWon(stats: PlayerStats): number {
  return Math.max(0, stats.timesBid - stats.timesSet);
}

/** Share of hands in which this player won the bid. */
export function bidFrequency(stats: PlayerStats): number | null {
  if (stats.handsPlayed <= 0) return null;
  return stats.timesBid / stats.handsPlayed;
}

export function winRate(stats: PlayerStats): number | null {
  if (stats.gamesPlayed <= 0) return null;
  return stats.gamesWon / stats.gamesPlayed;
}

export function averageBidValue(stats: PlayerStats): number | null {
  if (stats.bidValueCount <= 0) return null;
  return stats.bidValueSum / stats.bidValueCount;
}

export function biddingPointsAverage(stats: PlayerStats): number | null {
  if (stats.handsBidding <= 0) return null;
  return stats.pointsBidding / stats.handsBidding;
}

export function defendingPointsAverage(stats: PlayerStats): number | null {
  if (stats.handsDefending <= 0) return null;
  return stats.pointsDefending / stats.handsDefending;
}

export function recentBidForm(stats: PlayerStats): {
  sampleSize: number;
  successRate: number | null;
  setRate: number | null;
} {
  const sampleSize = stats.recentBidOutcomes.length;
  if (sampleSize <= 0) {
    return { sampleSize: 0, successRate: null, setRate: null };
  }
  const made = stats.recentBidOutcomes.reduce((a, b) => a + b, 0);
  const successRate = made / sampleSize;
  return { sampleSize, successRate, setRate: 1 - successRate };
}

export function modeWinRate(played: number, won: number): number | null {
  if (played <= 0) return null;
  return won / played;
}

export function formatPercent(rate: number | null): string {
  if (rate === null) return "—";
  return `${Math.round(rate * 100)}%`;
}

export function formatAverage(avg: number | null): string {
  if (avg === null) return "—";
  const rounded = Math.round(avg * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}
